/**
 * TypeScript API interfaces for OAR (Office Action Response) Task Management
 *
 * Generated from: OarController.java (Lines 118-362)
 * Controller path: /ai-oar
 *
 * This file contains TypeScript interfaces for:
 * 1. Create OAR task and upload PDF
 * 2. Get OAR task information
 * 3. Parse office action header
 * 4. Get office action header
 * 5. Parse office action table
 * 6. Get office action table
 * 7. Parse office action body
 * 8. Get office action body
 */

// ==================== 1. Create OAR Task API ====================

/**
 * Path parameters for createTaskWithPdf API
 */
export interface CreateTaskWithPdfPathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Upload parameters for createTaskWithPdf API
 */
export interface CreateTaskWithPdfUploadParams {
    /** PDF file */
    file: File;
}

/**
 * Combined request parameters for createTaskWithPdf
 */
export interface CreateTaskWithPdfRequestParams {
    path: CreateTaskWithPdfPathParams;
    upload: CreateTaskWithPdfUploadParams;
}

/**
 * Response data for createTaskWithPdf
 */
export interface OarTaskCreateResponseData {
    /** Task ID */
    task_id: string;
    /** PDF file S3 storage key */
    pdf_s3_key: string;
    /** PDF file access URL */
    pdf_url: string | null;
    /** Creation timestamp in milliseconds */
    created_at: number;
}

// ==================== 2. Get Task Info API ====================

/**
 * Path parameters for getTaskInfo API
 */
export interface GetTaskInfoPathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Combined request parameters for getTaskInfo
 */
export interface GetTaskInfoRequestParams {
    path: GetTaskInfoPathParams;
}

/**
 * PDF file information
 */
export interface PdfInfo {
    /** PDF file S3 storage key */
    s3_key: string;
    /** PDF file access URL with signature */
    pdf_url: string;
    /** PDF file name */
    file_name: string;
    /** PDF file size in bytes */
    file_size: number;
    /** Upload timestamp in milliseconds */
    uploaded_at: number;
}

/**
 * Response data for getTaskInfo
 */
export interface OarTaskInfoResponseData {
    /** Task ID */
    task_id: string;
    /** PDF file information */
    pdf_info?: PdfInfo;
    /** Header information parse status */
    header_parse_status?: 'NOT_STARTED' | 'PARSING' | 'SUCCESS' | 'FAILED';
    /** Table information parse status */
    table_parse_status?: 'NOT_STARTED' | 'PARSING' | 'SUCCESS' | 'FAILED';
    /** Body content parse status */
    body_parse_status?: 'NOT_STARTED' | 'PARSING' | 'SUCCESS' | 'FAILED';
    /** Creation timestamp in milliseconds */
    created_at: number;
    /** Update timestamp in milliseconds */
    updated_at: number;
}

// ==================== 3. Parse Office Action Header API ====================

/**
 * Path parameters for parseOfficeActionHeader API
 */
export interface ParseOfficeActionHeaderPathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Combined request parameters for parseOfficeActionHeader
 */
export interface ParseOfficeActionHeaderRequestParams {
    path: ParseOfficeActionHeaderPathParams;
}

/**
 * Patent agency information
 */
export interface AgencyInfo {
    /** Agency name */
    name: string;
    /** Agency address */
    address: string;
    /** Contact person name */
    contact_person: string;
    /** Contact phone number */
    phone_number: string;
}

/**
 * Response data for parseOfficeActionHeader and getOfficeActionHeader
 */
export interface OfficeActionHeaderResponseData {
    /** Document type title */
    document_type: string;
    /** Examination authority name */
    authority: string;
    /** Document number */
    document_number: string;
    /** Issue date */
    issue_date: string;
    /** Patent agency information */
    agency?: AgencyInfo;
    /** Application or patent number */
    application_number: string;
    /** Document sequence number */
    document_sequence_number: string;
    /** Applicant or patent holder */
    applicant: string;
    /** Invention title */
    invention_title: string;
    /** Document barcode information */
    barcodes: string[];
}

// ==================== 4. Get Office Action Header API ====================

/**
 * Path parameters for getOfficeActionHeader API
 */
export interface GetOfficeActionHeaderPathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Combined request parameters for getOfficeActionHeader
 */
export interface GetOfficeActionHeaderRequestParams {
    path: GetOfficeActionHeaderPathParams;
}

// ==================== 5. Parse Office Action Table API ====================

/**
 * Path parameters for parseOfficeActionTable API
 */
export interface ParseOfficeActionTablePathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Combined request parameters for parseOfficeActionTable
 */
export interface ParseOfficeActionTableRequestParams {
    path: ParseOfficeActionTablePathParams;
}

/**
 * Cited document information
 */
export interface CitedDocumentInfo {
    /** Cited document number */
    number: string;
    /** Document number or name */
    document_number_or_name: string;
    /** Publication date */
    publication_date: string;
    /** Whether it is a patent document */
    is_patent: boolean;
    /** Document text (for non-patent literature) */
    document_text?: string;
}

/**
 * Conclusive opinion information
 */
export interface ConclusiveOpinionInfo {
    /** Patent office */
    patent_office: string;
    /** Article number */
    article_number: string;
    /** Article title */
    article_title: string;
    /** Issue nature: substantive (实质性问题) or formal (形式性问题) */
    issue_nature: string;
    /** Whether selected */
    is_selected: boolean;
    /** Whether related to claims */
    is_claim_related: boolean;
    /** Affected claim numbers */
    affected_claims: string[];
    /** Other notes */
    other_notes?: string;
    /** Opinion ID */
    opinion_id: string;
}

/**
 * Examiner conclusion information
 */
export interface ExaminerConclusionInfo {
    /** Patent office */
    patent_office: string;
    /** Conclusion text */
    conclusion_text: string;
}

/**
 * Response data for parseOfficeActionTable and getOfficeActionTable
 */
export interface OfficeActionTableInfoResponseData {
    /** List of cited documents */
    cited_documents: CitedDocumentInfo[];
    /** List of conclusive opinions */
    conclusive_opinions: ConclusiveOpinionInfo[];
    /** List of examiner conclusions */
    examiner_conclusions: ExaminerConclusionInfo[];
}

// ==================== 6. Get Office Action Table API ====================

/**
 * Path parameters for getOfficeActionTable API
 */
export interface GetOfficeActionTablePathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Combined request parameters for getOfficeActionTable
 */
export interface GetOfficeActionTableRequestParams {
    path: GetOfficeActionTablePathParams;
}

// ==================== 7. Parse Office Action Body API ====================

/**
 * Path parameters for parseOfficeActionBody API
 */
export interface ParseOfficeActionBodyPathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Combined request parameters for parseOfficeActionBody
 */
export interface ParseOfficeActionBodyRequestParams {
    path: ParseOfficeActionBodyPathParams;
}

/**
 * Body section information
 */
export interface BodySectionInfo {
    /** Legal basis description */
    legal_basis: string;
    /** Affected claim numbers */
    affected_claims: string[];
    /** Claim text */
    claim_text: string;
    /** Cited document numbers */
    cited_document_numbers: string[];
    /** Technical comparison analysis */
    technical_comparison: string;
    /** Conclusion statement */
    conclusion_statement: string;
    /** Amendment suggestion */
    amendment_suggestion?: string;
    /** Full section text */
    full_text: string;
}

/**
 * Response data for parseOfficeActionBody and getOfficeActionBody
 */
export interface OfficeActionBodyContentResponseData {
    /** Original body text extracted from PDF */
    body_text: string;
    /** Structured body sections */
    body_sections: BodySectionInfo[];
}

// ==================== 8. Get Office Action Body API ====================

/**
 * Path parameters for getOfficeActionBody API
 */
export interface GetOfficeActionBodyPathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Combined request parameters for getOfficeActionBody
 */
export interface GetOfficeActionBodyRequestParams {
    path: GetOfficeActionBodyPathParams;
}

// ==================== 9. Analyze Claims API ====================

/**
 * Path parameters for analyzeClaims API
 */
export interface AnalyzeClaimsPathParams {
    /** Task ID */
    task_id: string;
    /** Conclusive opinion ID (序号) */
    conclusive_opinion_id: number;
}

/**
 * Combined request parameters for analyzeClaims
 */
export interface AnalyzeClaimsRequestParams {
    path: AnalyzeClaimsPathParams;
}

/**
 * Claim analysis result information
 */
export interface ClaimAnalysisResultInfo {
    /** Involved claim numbers */
    claim_involved: string;
    /** Examiner's technical comparison */
    examiner_comparison: string;
    /** Verification conclusion */
    verification_conclusion: string;
    /** Disclosed vs common knowledge analysis */
    disclosed_common_knowledge_not_disclosed: string;
    /** Argument strength: 强/中/弱 */
    argument_strength: string;
    /** Recommended action */
    recommended_action: string;
    /** Strategy recommendation (Markdown format) */
    strategy_recommendation: string;
    /** Argument features (comma-separated) */
    argument_features: string;
    /** Whether has warning */
    has_warning: boolean;
    /** Whether is strong argument */
    is_strong_argument: boolean;
    /** Whether is weak argument */
    is_weak_argument: boolean;
}

/**
 * Response data for analyzeClaims
 */
export interface ClaimAnalysisResponseData {
    /** Task ID */
    task_id: string;
    /** Conclusive opinion ID */
    conclusive_opinion_id: string;
    /** Conclusive opinion title */
    conclusive_opinion_title: string;
    /** Analysis results */
    results: ClaimAnalysisResultInfo[];
    /** Total claims count */
    total_claims: number;
}

// ==================== 10. Async Claim Analysis APIs ====================

/**
 * Path parameters for submitClaimAnalysisTask API
 */
export interface SubmitClaimAnalysisTaskPathParams {
    /** Task ID */
    task_id: string;
    /** Conclusive opinion ID (序号) */
    conclusive_opinion_id: string;
}

/**
 * Combined request parameters for submitClaimAnalysisTask
 */
export interface SubmitClaimAnalysisTaskRequestParams {
    path: SubmitClaimAnalysisTaskPathParams;
}

/**
 * Response data for submitClaimAnalysisTask
 */
export interface ClaimAnalysisTaskSubmitResponseData {
    /** Async task ID */
    async_task_id: string;
    /** Task status */
    status: string;
    /** Status message */
    message: string;
    /** Status query URL */
    status_url: string;
    /** Result query URL */
    result_url: string;
}

/**
 * Path parameters for getClaimAnalysisTaskStatus API
 */
export interface GetClaimAnalysisTaskStatusPathParams {
    /** Async task ID */
    async_task_id: string;
}

/**
 * Combined request parameters for getClaimAnalysisTaskStatus
 */
export interface GetClaimAnalysisTaskStatusRequestParams {
    path: GetClaimAnalysisTaskStatusPathParams;
}

/**
 * Task status enum
 */
export type ClaimAnalysisTaskStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

/**
 * Response data for getClaimAnalysisTaskStatus
 */
export interface ClaimAnalysisTaskStatusData {
    /** Async task ID */
    async_task_id: string;
    /** Original task ID */
    task_id: string;
    /** Conclusive opinion ID */
    conclusive_opinion_id: number;
    /** Task status */
    status: ClaimAnalysisTaskStatus;
    /** Status description */
    status_description: string;
    /** Task creation time */
    create_time: string;
    /** Task update time */
    update_time: string;
    /** Duration in seconds */
    duration_in_seconds: number;
    /** Error message (if failed) */
    error_message?: string;
    /** Whether task is finished */
    is_finished: boolean;
    /** Whether task can be cancelled */
    is_cancellable: boolean;
    /** Whether task completed successfully */
    is_successful: boolean;
    /** Result URL (if successful) */
    result_url?: string;
}

/**
 * Path parameters for getClaimAnalysisTaskResult API
 */
export interface GetClaimAnalysisTaskResultPathParams {
    /** Async task ID */
    async_task_id: string;
}

/**
 * Combined request parameters for getClaimAnalysisTaskResult
 */
export interface GetClaimAnalysisTaskResultRequestParams {
    path: GetClaimAnalysisTaskResultPathParams;
}

/**
 * Path parameters for cancelClaimAnalysisTask API
 */
export interface CancelClaimAnalysisTaskPathParams {
    /** Async task ID */
    async_task_id: string;
}

/**
 * Combined request parameters for cancelClaimAnalysisTask
 */
export interface CancelClaimAnalysisTaskRequestParams {
    path: CancelClaimAnalysisTaskPathParams;
}

// ==================== 11. Statement Generation and Export APIs ====================

/**
 * Statement generation status enum
 */
export enum StatementGenerationStatusEnum {
    /** Generating */
    GENERATING = 'GENERATING',
    /** Generation successful */
    SUCCESS = 'SUCCESS',
    /** Generation failed */
    FAILED = 'FAILED',
}

/**
 * Path parameters for generateStatement API
 */
export interface GenerateStatementPathParams {
    /** Task ID */
    task_id: string;
}

/**
 * Combined request parameters for generateStatement
 */
export interface GenerateStatementRequestParams {
    path: GenerateStatementPathParams;
}

/**
 * Response data for generateStatement
 */
export interface StatementGenerationResponseData {
    /** Statement ID */
    statement_id: string;
    /** Task ID */
    task_id: string;
    /** Generation status */
    generation_status: StatementGenerationStatusEnum;
    /** Statement title */
    title?: string;
    /** Statement content (if immediately available) */
    content?: string;
    /** Export URL (if generation successful) */
    export_url?: string;
    /** Creation time */
    created_at: number;
    /** Update time */
    updated_at: number;
}

/**
 * Path parameters for exportStatement API
 */
export interface ExportStatementPathParams {
    /** Task ID */
    task_id: string;
    /** Statement ID */
    statement_id: string;
}

/**
 * Combined request parameters for exportStatement
 */
export interface ExportStatementRequestParams {
    path: ExportStatementPathParams;
}

/**
 * Response data for exportStatement
 */
export interface ExportStatementResponseData {
    /** Download URL for the Word document */
    download_url: string;
    /** File name */
    file_name: string;
    /** File size in bytes */
    file_size?: number;
}

/**
 * Path parameters for getStatementStatus API
 */
export interface GetStatementStatusPathParams {
    /** Task ID */
    task_id: string;
    /** Statement ID */
    statement_id: string;
}

/**
 * Combined request parameters for getStatementStatus
 */
export interface GetStatementStatusRequestParams {
    path: GetStatementStatusPathParams;
}

// ==================== API Interface ====================

/**
 * OAR Task Management API
 *
 * Provides endpoints for managing Office Action Response tasks including:
 * - Task creation with PDF upload
 * - Task information retrieval
 * - Header, table, and body content parsing and retrieval
 * - Claim analysis (sync and async)
 */
export interface OarTaskApi {
    /**
   * Create OAR task and upload PDF
   *
   * Creates a new OAR task and uploads the office action notice PDF file
   *
   * @param params - Request parameters including path and upload
   * @returns Promise resolving to task creation response
   */
    createTaskWithPdf(params: CreateTaskWithPdfRequestParams): Promise<OarTaskCreateResponseData>;

    /**
   * Get OAR task information
   *
   * Retrieves detailed information about an OAR task including PDF file information
   *
   * @param params - Request parameters including path (task_id)
   * @returns Promise resolving to task information
   */
    getTaskInfo(params: GetTaskInfoRequestParams): Promise<OarTaskInfoResponseData>;

    /**
   * Parse office action header
   *
   * Parses the PDF file in the OAR task and extracts/persists header metadata
   *
   * @param params - Request parameters including path (task_id)
   * @returns Promise resolving to parsed header information
   */
    parseOfficeActionHeader(params: ParseOfficeActionHeaderRequestParams): Promise<OfficeActionHeaderResponseData>;

    /**
   * Get office action header
   *
   * Retrieves the office action header metadata by task ID
   *
   * @param params - Request parameters including path (task_id)
   * @returns Promise resolving to header information
   */
    getOfficeActionHeader(params: GetOfficeActionHeaderRequestParams): Promise<OfficeActionHeaderResponseData>;

    /**
   * Parse office action table
   *
   * Parses the PDF file in the OAR task and extracts/persists table information
   * including cited documents, conclusive opinions, and examiner conclusions
   *
   * @param params - Request parameters including path (task_id)
   * @returns Promise resolving to parsed table information
   */
    parseOfficeActionTable(params: ParseOfficeActionTableRequestParams): Promise<OfficeActionTableInfoResponseData>;

    /**
   * Get office action table
   *
   * Retrieves the office action table information by task ID
   *
   * @param params - Request parameters including path (task_id)
   * @returns Promise resolving to table information
   */
    getOfficeActionTable(params: GetOfficeActionTableRequestParams): Promise<OfficeActionTableInfoResponseData>;

    /**
   * Parse office action body
   *
   * Parses the PDF file in the OAR task and extracts/persists body content
   * including structured sections, summary, and procedure instructions
   *
   * @param params - Request parameters including path (task_id)
   * @returns Promise resolving to parsed body content
   */
    parseOfficeActionBody(params: ParseOfficeActionBodyRequestParams): Promise<OfficeActionBodyContentResponseData>;

    /**
   * Get office action body
   *
   * Retrieves the office action body content by task ID
   *
   * @param params - Request parameters including path (task_id)
   * @returns Promise resolving to body content
   */
    getOfficeActionBody(params: GetOfficeActionBodyRequestParams): Promise<OfficeActionBodyContentResponseData>;

    /**
   * Analyze claims for a specific conclusive opinion
   *
   * Analyzes the claims related to a conclusive opinion and provides
   * detailed examination comparison, verification conclusions, and strategy recommendations
   *
   * @param params - Request parameters including path (task_id, conclusive_opinion_id)
   * @returns Promise resolving to claim analysis results
   */
    analyzeClaims(params: AnalyzeClaimsRequestParams): Promise<ClaimAnalysisResponseData>;

    /**
   * Submit async claim analysis task
   *
   * Submits an asynchronous task to analyze claims for a specific conclusive opinion.
   * Returns immediately with a task ID for status polling.
   *
   * @param params - Request parameters including path (task_id, conclusive_opinion_id)
   * @returns Promise resolving to task submission response with async_task_id
   */
    submitClaimAnalysisTask(params: SubmitClaimAnalysisTaskRequestParams): Promise<ClaimAnalysisTaskSubmitResponseData>;

    /**
   * Get claim analysis task status
   *
   * Queries the current status of an asynchronous claim analysis task.
   * Used for polling task progress.
   *
   * @param params - Request parameters including path (async_task_id)
   * @returns Promise resolving to task status information
   */
    getClaimAnalysisTaskStatus(params: GetClaimAnalysisTaskStatusRequestParams): Promise<ClaimAnalysisTaskStatusData>;

    /**
   * Get claim analysis task result
   *
   * Retrieves the final result of a completed claim analysis task.
   * Only succeeds when task status is COMPLETED.
   *
   * @param params - Request parameters including path (async_task_id)
   * @returns Promise resolving to claim analysis results
   */
    getClaimAnalysisTaskResult(params: GetClaimAnalysisTaskResultRequestParams): Promise<ClaimAnalysisResponseData>;

    /**
   * Cancel claim analysis task
   *
   * Cancels a pending claim analysis task. Only works for tasks in PENDING status.
   *
   * @param params - Request parameters including path (async_task_id)
   * @returns Promise resolving when task is cancelled
   */
    cancelClaimAnalysisTask(params: CancelClaimAnalysisTaskRequestParams): Promise<void>;
}

/**
 * Symbol for dependency injection
 */
export const OarTaskApi = Symbol.for('OarTaskApi');
